import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import { trackedDownloadUrl } from "@/lib/downloadTracking";
import { FileText, ExternalLink, Calendar, Users, Microscope, Filter, Shield, Sparkles, ArrowRight, FlaskConical, Bone, Hand, Award, Heart, Zap, ChevronDown } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { 
  getAllStudies, 
  getStudiesByCategory, 
  getTranslatedText, 
  getTypeLabel as getTypeLabelFromData,
  categoryLabels as categoryLabelsData,
  getStudyCountByCategory,
  getStudyRelevance,
  getRelevanceColor,
  getRelevanceLabel,
  type Language,
  type Study,
  type TranslatedText
} from "@/data/researchStudies";

const translations = {
  de: {
    title: "Wissenschaftliche Studien | EAP® Abutments",
    description: "Aktuelle PubMed-Studien zu Abutment-Materialien, Kunststoffen und Zellanlagerung in der Implantologie.",
    heading: "Wissenschaftliche Studien",
    subtitle: "Aktuelle Forschung zu Abutment-Materialien & Zellanlagerung",
    intro: "Die folgenden Studien aus PubMed und Fachzeitschriften befassen sich mit dem Vergleich verschiedener Abutment-Materialien (Titan, Zirkon, PEEK, PMMA) hinsichtlich Zellanhaftung, Weichgewebsintegration und Biokompatibilität.",
    readStudy: "Studie lesen",
    problemStatement: "Problemstellung",
    purpose: "Ziel",
    methods: "Material & Methoden",
    results: "Ergebnisse",
    conclusions: "Schlussfolgerungen",
    evidenceSummary: "Evidenz-Zusammenfassung: Die 8 Kategorien",
    peerReviewedStudies: "peer-reviewed Studien aus",
    pubmedJournals: "PubMed & Fachzeitschriften",
    polymerToxicity: "Resin-Materialien & Zellinteraktion",
    polymerDesc: "In-vitro-Studien zu TEGDMA, Bis-GMA, HEMA",
    highestToxicity: "In-vitro-Befunde",
    cementProblem: "Residualzement & periimplantäre Befunde",
    cementDesc: "Studien zum Zusammenhang von überschüssigem Zement und periimplantären Befunden",
    residue: "der Teststellen",
    moreLoss: "vs. 0,07 mm",
    cellAttachment: "Zellanhaftung",
    cellDesc: "Studien zu Titan und periimplantärem Weichgewebe",
    adhesion: "Adhäsion",
    hydrophilic: "Hydrophil",
    oneAbutment: "One-Abutment-One-Time",
    manipulationDesc: "Meta-Analysen zu definitiven Abutments und marginalem Knochenverlust",
    bone: "Knochen",
    placement: "Einsetzen",
    surfaceModification: "Oberflächenmodifikation",
    surfaceDesc: "Anodisierung, TiO₂-Nanoröhren, hydrophile Oberflächen",
    anodized: "Anodisiert",
    bioactive: "Bioaktiv",
    emergenceProfile: "Emergenzprofil",
    emergenceDesc: "Patientenspezifische Geometrie, ästhetische Outcomes",
    individual: "Individuell",
    aesthetic: "Ästhetik",
    allergies: "Allergien & Biokompatibilität",
    allergiesDesc: "Studien zu Titanverträglichkeit, Metallallergien",
    titanium: "Titan",
    safe: "Verträglichkeit",
    conversionDegree: "Konversionsgrad",
    conversionDesc: "Polymerisation, Restmonomere, 3D-Druck",
    incomplete: "Unvollständig",
    release: "Freisetzung",
    footnoteSubcategory: "Studien sind Teil übergeordneter Kategorien (C: Zellanhaftung, D: One-Abutment, A: Resin-Materialien)",
    scientificSolution: "Konstruktionsprinzipien des EAP®-Konzepts (Herstellerangaben)",
    pureTitanium: "Reines Titan",
    pureTitaniumDesc: "Im gesamten Gingivakontaktbereich – keine Polymere im Weichgewebe",
    optimalCementation: "Position der Klebefuge",
    optimalCementationDesc: "Gerades Emergenzprofil, supragingivale Ränder, konstruktiv möglichst großer Abstand zum Knochen",
    hydrophilicSurface: "Hydrophile Oberfläche",
    hydrophilicSurfaceDesc: "Rauheit und Benetzbarkeit werden in Studien im Zusammenhang mit Zellanhaftung untersucht",
    oneTimeProtocol: "One-Time Protokoll",
    oneTimeProtocolDesc: "Definitives Abutment bei Insertion – keine wiederholte Manipulation",
    registerNow: "Jetzt registrieren",
    requestConsultation: "Kostenlose Beratung & Muster anfordern",
    scrollDown: "↓ Scrollen Sie nach unten für detaillierte Analysen jeder Kategorie mit Quellenangaben und Statistiken.",
    filterByCategory: "Nach Kategorie filtern:",
    studiesInCategory: "Studien in der Kategorie",
    keyFindingsTitle: "Kernerkenntnisse aus der Forschung",
    keyFindingsSubtitle: "Ergebnisse einzelner Untersuchungen – keine Garantie und keine Aussage über den Einzelfall",
    finding1Title: "Zementreste und periimplantäre Befunde",
    finding1Text: "In der Studie wurde überschüssiger Zement an 81 % der untersuchten Teststellen mit klinischen und/oder radiologischen Zeichen periimplantärer Erkrankung gefunden. Die Studie beschreibt eine Assoziation und keinen universellen Kausalnachweis.",
    finding1Source: "Wilson TG Jr. (2009)",
    finding2Title: "Knochenverlust und Methacrylat-Zemente",
    finding2Text: "In dieser retrospektiven Analyse war der periimplantäre Knochenverlust bei einem Methacrylat-Zement mit Zementüberschuss höher (1,37 mm) als bei einem Zinkoxid-Eugenol-Zement (0,07 mm).",
    finding2Source: "Korsch M, Walther W (2015)",
    finding3Title: "Zytotoxizität von Monomeren",
    finding3Text: "In-vitro-Untersuchungen beschreiben unter definierten experimentellen Bedingungen zytotoxische Effekte von Bis-GMA, TEGDMA und HEMA auf Fibroblasten und Osteoblasten. In-vitro-Befunde sind nicht automatisch klinische Effekte.",
    finding3Source: "Schweikl H et al. (2006)",
    finding4Title: "One-Abutment-One-Time Vorteile",
    finding4Text: "Die Meta-Analyse berichtet im Mittel 0,19 mm weniger marginalen Knochenverlust bei definitiven Abutments – ein Studienmittelwert, keine Aussage über den Einzelfall.",
    finding4Source: "Koutouzis T et al. (2017)",
    finding5Title: "Titan-Oberflächenoptimierung",
    finding5Text: "In Studien wurden für hydrophile Titanoberflächen günstigere Werte der Gewebeintegration als für konventionelle Oberflächen berichtet.",
    finding5Source: "Schwarz F et al. (2009)",
    advantagesTitle: "Konstruktionsmerkmale patientenspezifischer EAP® Abutments (Herstellerangaben)",
    advantage1: "Patientenspezifisches Emergenzprofil – anatomisch wie ein natürlicher Zahn",
    advantage2: "Klebefuge konstruktiv möglichst weit vom Knochen entfernt – ein Konstruktionsmerkmal, keine nachgewiesene Risikoreduktion",
    advantage3: "Ästhetische und weichgewebsbezogene Aspekte – keine sichtbaren grauen Ränder vorgesehen",
    advantage4: "Einfacher One-Abutment-One-Time Workflow – weniger Sitzungen",
    researchBegin2009: "2009 – Beginn der EAP® Forschung",
    researchBegin2009Desc: "Die wissenschaftliche Grundlage für patientenspezifische Abutments",
    downloadSummary: "📄 Studienzusammenfassung (3-seitiges HTML-Dokument)",
    downloadHint: "Stand: Oktober 2026 – 155 in Fachdatenbanken (PubMed, Europe PMC, Crossref) verifizierte Studien zu zugrunde liegenden Prinzipien; keine produktspezifischen EAP®-Studien",
  },
  en: {
    title: "Scientific Studies | EAP® Abutments",
    description: "Current PubMed studies on abutment materials, polymers, and cell attachment in implantology.",
    heading: "Scientific Studies",
    subtitle: "Current Research on Abutment Materials & Cell Attachment",
    intro: "The following studies from PubMed and scientific journals examine the comparison of different abutment materials regarding cell adhesion, soft tissue integration, and biocompatibility.",
    readStudy: "Read Study",
    problemStatement: "Statement of Problem",
    purpose: "Purpose",
    methods: "Material & Methods",
    results: "Results",
    conclusions: "Conclusions",
    evidenceSummary: "Evidence Summary: The 8 Categories",
    peerReviewedStudies: "peer-reviewed studies from",
    pubmedJournals: "PubMed & Scientific Journals",
    polymerToxicity: "Resin Materials & Cell Interaction",
    polymerDesc: "in-vitro studies on TEGDMA, Bis-GMA, HEMA",
    highestToxicity: "in-vitro findings",
    cementProblem: "Residual Cement & Peri-Implant Findings",
    cementDesc: "studies on the association between excess cement and peri-implant findings",
    residue: "of test sites",
    moreLoss: "vs. 0.07 mm",
    cellAttachment: "Cell Attachment",
    cellDesc: "studies on titanium and peri-implant soft tissue",
    adhesion: "Adhesion",
    hydrophilic: "Hydrophilic",
    oneAbutment: "One-Abutment-One-Time",
    manipulationDesc: "meta-analyses on definitive abutments and marginal bone loss",
    bone: "Bone",
    placement: "Placement",
    surfaceModification: "Surface Modification",
    surfaceDesc: "Anodization, TiO₂ nanotubes, hydrophilic surfaces",
    anodized: "Anodized",
    bioactive: "Bioactive",
    emergenceProfile: "Emergence Profile",
    emergenceDesc: "Patient-specific geometry, aesthetic outcomes",
    individual: "Individual",
    aesthetic: "Aesthetic",
    allergies: "Allergies & Biocompatibility",
    allergiesDesc: "studies on titanium tolerance, metal allergies",
    titanium: "Titanium",
    safe: "Tolerance",
    conversionDegree: "Conversion Degree",
    conversionDesc: "Polymerization, residual monomers, 3D printing",
    incomplete: "Incomplete",
    release: "Release",
    footnoteSubcategory: "Studies are part of parent categories (C: Cell Attachment, D: One-Abutment, A: Resin Materials)",
    scientificSolution: "Design Principles of the EAP® Concept (manufacturer information)",
    pureTitanium: "Pure Titanium",
    pureTitaniumDesc: "In the entire gingival contact area – no polymers in soft tissue",
    optimalCementation: "Position of the Adhesive Joint",
    optimalCementationDesc: "Straight emergence profile, supragingival margins, greatest distance to bone the design allows",
    hydrophilicSurface: "Hydrophilic Surface",
    hydrophilicSurfaceDesc: "Roughness and wettability are studied in relation to cell attachment",
    oneTimeProtocol: "One-Time Protocol",
    oneTimeProtocolDesc: "Definitive abutment at insertion – no repeated manipulation",
    registerNow: "Register Now",
    requestConsultation: "Request free consultation & samples",
    scrollDown: "↓ Scroll down for detailed analyses of each category with references and statistics.",
    filterByCategory: "Filter by category:",
    studiesInCategory: "studies in category",
    keyFindingsTitle: "Key Research Findings",
    keyFindingsSubtitle: "Results of individual studies – not guarantees for individual outcomes",
    finding1Title: "Cement Residue in Peri-Implantitis",
    finding1Text: "In the study, excess cement was found at 81% of the examined test sites showing clinical and/or radiographic signs of peri-implant disease. The study describes an association, not universal causation.",
    finding1Source: "Wilson TG Jr. (2009)",
    finding2Title: "Bone Loss from Methacrylate Cements",
    finding2Text: "In this retrospective analysis, peri-implant bone loss was higher with a methacrylate cement with excess cement (1.37 mm) than with a zinc oxide-eugenol cement (0.07 mm).",
    finding2Source: "Korsch M, Walther W (2015)",
    finding3Title: "Cytotoxicity of Monomers",
    finding3Text: "In-vitro investigations describe cytotoxic effects of Bis-GMA, TEGDMA and HEMA on fibroblasts and osteoblasts under defined experimental conditions. In-vitro findings are not automatically clinical outcomes.",
    finding3Source: "Schweikl H et al. (2006)",
    finding4Title: "One-Abutment-One-Time Benefits",
    finding4Text: "The meta-analysis reports on average 0.19 mm less marginal bone loss with definitive abutments – a study mean, not a statement about the individual case.",
    finding4Source: "Koutouzis T et al. (2017)",
    finding5Title: "Titanium Surface Optimization",
    finding5Text: "Studies reported more favourable tissue integration values for hydrophilic titanium surfaces than for conventional surfaces.",
    finding5Source: "Schwarz F et al. (2009)",
    advantagesTitle: "Design Features of Patient-Specific EAP® Abutments (manufacturer information)",
    advantage1: "Patient-specific emergence profile – anatomically like a natural tooth",
    advantage2: "Adhesive joint positioned as far from the bone as the design allows – a design feature, not a proven risk reduction",
    advantage3: "Aesthetic and soft-tissue related aspects – no visible gray margins intended",
    advantage4: "Simple One-Abutment-One-Time workflow – fewer sessions",
    researchBegin2009: "2009 – Beginning of EAP® Research",
    researchBegin2009Desc: "The scientific foundation for patient-specific abutments",
    downloadSummary: "📄 Study summary (3-page HTML document)",
    downloadHint: "Status: October 2026 – 155 database-verified (PubMed, Europe PMC, Crossref) studies on underlying principles; not product-specific EAP® studies",
  },
  fr: {
    title: "Études Scientifiques | EAP® Abutments",
    description: "Études PubMed actuelles sur les matériaux de piliers, polymères et adhésion cellulaire.",
    heading: "Études Scientifiques",
    subtitle: "Recherche actuelle sur les matériaux de piliers et l'adhésion cellulaire",
    intro: "Les études suivantes de PubMed examinent la comparaison de différents matériaux de piliers concernant l'adhésion cellulaire et la biocompatibilité.",
    readStudy: "Lire l'étude",
    problemStatement: "Problématique",
    purpose: "Objectif",
    methods: "Matériel & Méthodes",
    results: "Résultats",
    conclusions: "Conclusions",
    evidenceSummary: "Résumé des preuves : Les 8 catégories",
    peerReviewedStudies: "études évaluées par des pairs de",
    pubmedJournals: "PubMed & Revues scientifiques",
    polymerToxicity: "Matériaux résineux & interaction cellulaire",
    polymerDesc: "études in vitro sur TEGDMA, Bis-GMA, HEMA",
    highestToxicity: "résultats in vitro",
    cementProblem: "Ciment résiduel & constats péri-implantaires",
    cementDesc: "études sur l'association entre excès de ciment et constats péri-implantaires",
    residue: "des sites testés",
    moreLoss: "vs. 0,07 mm",
    cellAttachment: "Adhésion cellulaire",
    cellDesc: "études sur le titane et les tissus mous péri-implantaires",
    adhesion: "Adhésion",
    hydrophilic: "Hydrophile",
    oneAbutment: "One-Abutment-One-Time",
    manipulationDesc: "méta-analyses sur les piliers définitifs et la perte osseuse marginale",
    bone: "Os",
    placement: "Placement",
    surfaceModification: "Modification de surface",
    surfaceDesc: "Anodisation, nanotubes TiO₂, surfaces hydrophiles",
    anodized: "Anodisé",
    bioactive: "Bioactif",
    emergenceProfile: "Profil d'émergence",
    emergenceDesc: "Géométrie spécifique au patient, résultats esthétiques",
    individual: "Individuel",
    aesthetic: "Esthétique",
    allergies: "Allergies & Biocompatibilité",
    allergiesDesc: "études sur tolérance au titane, allergies aux métaux",
    titanium: "Titane",
    safe: "Tolérance",
    conversionDegree: "Degré de conversion",
    conversionDesc: "Polymérisation, monomères résiduels, impression 3D",
    incomplete: "Incomplet",
    release: "Libération",
    footnoteSubcategory: "Études font partie des catégories parentes (C: Adhésion cellulaire, D: One-Abutment, A: Matériaux résineux)",
    scientificSolution: "Principes de conception du concept EAP® (informations du fabricant)",
    pureTitanium: "Titane pur",
    pureTitaniumDesc: "Dans toute la zone de contact gingival – pas de polymères dans les tissus mous",
    optimalCementation: "Position du joint de collage",
    optimalCementationDesc: "Profil d'émergence droit, marges supragingivales, distance à l'os la plus grande que la conception permet",
    hydrophilicSurface: "Surface hydrophile",
    hydrophilicSurfaceDesc: "La rugosité et la mouillabilité sont étudiées en lien avec l'adhésion cellulaire",
    oneTimeProtocol: "Protocole One-Time",
    oneTimeProtocolDesc: "Pilier définitif à l'insertion – pas de manipulation répétée",
    registerNow: "S'inscrire maintenant",
    requestConsultation: "Demander une consultation gratuite & échantillons",
    scrollDown: "↓ Faites défiler pour des analyses détaillées de chaque catégorie.",
    filterByCategory: "Filtrer par catégorie :",
    studiesInCategory: "études dans la catégorie",
    keyFindingsTitle: "Résultats clés de la recherche",
    keyFindingsSubtitle: "Résultats d'études individuelles – aucune garantie pour le cas individuel",
    finding1Title: "Résidus de ciment dans la péri-implantite",
    finding1Text: "Dans l'étude, un excès de ciment a été trouvé sur 81 % des sites examinés présentant des signes cliniques et/ou radiologiques de maladie péri-implantaire. L'étude décrit une association, pas une causalité universelle.",
    finding1Source: "Wilson TG Jr. (2009)",
    finding2Title: "Perte osseuse due aux ciments méthacrylate",
    finding2Text: "Dans cette analyse rétrospective, la perte osseuse péri-implantaire était plus élevée avec un ciment méthacrylate présentant un excès de ciment (1,37 mm) qu'avec un ciment oxyde de zinc-eugénol (0,07 mm).",
    finding2Source: "Korsch M, Walther W (2015)",
    finding3Title: "Cytotoxicité des monomères",
    finding3Text: "Des études in vitro décrivent des effets cytotoxiques du Bis-GMA, du TEGDMA et du HEMA dans des conditions expérimentales définies. Les résultats in vitro ne sont pas automatiquement des effets cliniques.",
    finding3Source: "Schweikl H et al. (2006)",
    finding4Title: "Avantages One-Abutment-One-Time",
    finding4Text: "La méta-analyse rapporte en moyenne 0,19 mm de perte osseuse marginale en moins avec des piliers définitifs – une moyenne d'étude, pas une affirmation sur le cas individuel.",
    finding4Source: "Koutouzis T et al. (2017)",
    finding5Title: "Optimisation de la surface du titane",
    finding5Text: "Des études rapportent des valeurs d'intégration tissulaire plus favorables pour les surfaces de titane hydrophiles que pour les surfaces conventionnelles.",
    finding5Source: "Schwarz F et al. (2009)",
    advantagesTitle: "Caractéristiques de conception des piliers EAP® (informations du fabricant)",
    advantage1: "Profil d'émergence spécifique – anatomiquement comme une dent naturelle",
    advantage2: "Joint de collage aussi éloigné de l'os que la conception le permet – une caractéristique de conception, pas une réduction de risque prouvée",
    advantage3: "Aspects esthétiques et liés aux tissus mous – pas de bords gris visibles prévus",
    advantage4: "Workflow One-Abutment-One-Time simple – moins de séances",
    researchBegin2009: "2009 – Début de la recherche EAP®",
    researchBegin2009Desc: "La base scientifique pour les piliers spécifiques au patient",
    downloadSummary: "📄 Résumé des études (document HTML de 3 pages)",
    downloadHint: "État : Septembre 2026 – 155 études vérifiées dans des bases de données scientifiques sur des principes sous-jacents ; pas d'études spécifiques au produit EAP®",
  },
  nl: {
    title: "Wetenschappelijke Studies | EAP® Abutments",
    description: "Actuele PubMed-studies over abutmentmaterialen, kunststoffen en celaanhechting.",
    heading: "Wetenschappelijke Studies",
    subtitle: "Actueel onderzoek naar abutmentmaterialen en celaanhechting",
    intro: "De volgende studies onderzoeken de vergelijking van verschillende abutmentmaterialen met betrekking tot celadhesie en biocompatibiliteit.",
    readStudy: "Studie lezen",
    problemStatement: "Probleemstelling",
    purpose: "Doel",
    methods: "Materiaal & Methoden",
    results: "Resultaten",
    conclusions: "Conclusies",
    evidenceSummary: "Bewijsoverzicht: De 8 categorieën",
    peerReviewedStudies: "peer-reviewed studies uit",
    pubmedJournals: "PubMed & Wetenschappelijke tijdschriften",
    polymerToxicity: "Resin-materialen & celinteractie",
    polymerDesc: "in-vitrostudies over TEGDMA, Bis-GMA, HEMA",
    highestToxicity: "in-vitrobevindingen",
    cementProblem: "Restcement & peri-implantaire bevindingen",
    cementDesc: "studies over het verband tussen overtollig cement en peri-implantaire bevindingen",
    residue: "van de testlocaties",
    moreLoss: "vs. 0,07 mm",
    cellAttachment: "Celaanhechting",
    cellDesc: "studies over titanium en peri-implantaire weke delen",
    adhesion: "Adhesie",
    hydrophilic: "Hydrofiel",
    oneAbutment: "One-Abutment-One-Time",
    manipulationDesc: "meta-analyses over definitieve abutments en marginaal botverlies",
    bone: "Bot",
    placement: "Plaatsing",
    surfaceModification: "Oppervlaktemodificatie",
    surfaceDesc: "Anodisering, TiO₂-nanobuisjes, hydrofiele oppervlakken",
    anodized: "Geanodiseerd",
    bioactive: "Bioactief",
    emergenceProfile: "Emergentieprofiel",
    emergenceDesc: "Patiëntspecifieke geometrie, esthetische resultaten",
    individual: "Individueel",
    aesthetic: "Esthetiek",
    allergies: "Allergieën & Biocompatibiliteit",
    allergiesDesc: "studies over titaniumtolerantie, metaalallergieën",
    titanium: "Titanium",
    safe: "Verdraagbaarheid",
    conversionDegree: "Conversiegraad",
    conversionDesc: "Polymerisatie, restmonomeren, 3D-printen",
    incomplete: "Onvolledig",
    release: "Afgifte",
    footnoteSubcategory: "Studies zijn onderdeel van bovenliggende categorieën (C: Celaanhechting, D: One-Abutment, A: Resin-materialen)",
    scientificSolution: "Ontwerpprincipes van het EAP®-concept (informatie van de fabrikant)",
    pureTitanium: "Puur titanium",
    pureTitaniumDesc: "In het gehele gingivale contactgebied – geen polymeren in weke delen",
    optimalCementation: "Positie van de klefvoeg",
    optimalCementationDesc: "Recht emergentieprofiel, supragingivale randen, zo groot mogelijke afstand tot bot binnen het ontwerp",
    hydrophilicSurface: "Hydrofiel oppervlak",
    hydrophilicSurfaceDesc: "Ruwheid en bevochtigbaarheid worden onderzocht in relatie tot celaanhechting",
    oneTimeProtocol: "One-Time Protocol",
    oneTimeProtocolDesc: "Definitieve abutment bij insertie – geen herhaalde manipulatie",
    registerNow: "Nu registreren",
    requestConsultation: "Vraag gratis advies & monsters aan",
    scrollDown: "↓ Scroll naar beneden voor gedetailleerde analyses van elke categorie.",
    filterByCategory: "Filteren op categorie:",
    studiesInCategory: "studies in categorie",
    keyFindingsTitle: "Belangrijkste onderzoeksbevindingen",
    keyFindingsSubtitle: "Resultaten van afzonderlijke studies – geen garantie voor het individuele geval",
    finding1Title: "Cementresten bij peri-implantitis",
    finding1Text: "In de studie werd overtollig cement gevonden op 81 % van de onderzochte testlocaties met klinische en/of radiologische tekenen van peri-implantaire ziekte. De studie beschrijft een associatie, geen universeel causaal bewijs.",
    finding1Source: "Wilson TG Jr. (2009)",
    finding2Title: "Botverlies door methacrylaat-cementen",
    finding2Text: "In deze retrospectieve analyse was het peri-implantaire botverlies hoger bij een methacrylaatcement met cementoverschot (1,37 mm) dan bij een zinkoxide-eugenolcement (0,07 mm).",
    finding2Source: "Korsch M, Walther W (2015)",
    finding3Title: "Cytotoxiciteit van monomeren",
    finding3Text: "In-vitro-onderzoeken beschrijven cytotoxische effecten van Bis-GMA, TEGDMA en HEMA onder gedefinieerde experimentele omstandigheden. In-vitrobevindingen zijn niet automatisch klinische effecten.",
    finding3Source: "Schweikl H et al. (2006)",
    finding4Title: "One-Abutment-One-Time voordelen",
    finding4Text: "De meta-analyse rapporteert gemiddeld 0,19 mm minder marginaal botverlies bij definitieve abutments – een studiegemiddelde, geen uitspraak over het individuele geval.",
    finding4Source: "Koutouzis T et al. (2017)",
    finding5Title: "Titanium oppervlakte-optimalisatie",
    finding5Text: "Studies rapporteren gunstigere waarden voor weefselintegratie bij hydrofiele titaniumoppervlakken dan bij conventionele oppervlakken.",
    finding5Source: "Schwarz F et al. (2009)",
    advantagesTitle: "Ontwerpkenmerken van patiëntspecifieke EAP® abutments (informatie van de fabrikant)",
    advantage1: "Patiëntspecifiek emergentieprofiel – anatomisch als een natuurlijke tand",
    advantage2: "Klefvoeg zo ver van het bot als het ontwerp toelaat – een ontwerpkenmerk, geen bewezen risicoreductie",
    advantage3: "Esthetische en weke-delen-gerelateerde aspecten – geen zichtbare grijze randen voorzien",
    advantage4: "Eenvoudige One-Abutment-One-Time workflow – minder sessies",
    researchBegin2009: "2009 – Begin van EAP® Onderzoek",
    researchBegin2009Desc: "De wetenschappelijke basis voor patiëntspecifieke abutments",
    downloadSummary: "📄 Studieoverzicht (HTML-document van 3 pagina's)",
    downloadHint: "Status: October 2026 – 155 in vakdatabanken geverifieerde studies over onderliggende principes; geen productspecifieke EAP®-studies",
  },
  it: {
    title: "Studi Scientifici | EAP® Abutments",
    description: "Studi PubMed attuali sui materiali per abutment, polimeri e adesione cellulare.",
    heading: "Studi Scientifici",
    subtitle: "Ricerca attuale sui materiali per abutment e adesione cellulare",
    intro: "I seguenti studi esaminano il confronto di diversi materiali per abutment riguardo all'adesione cellulare e alla biocompatibilità.",
    readStudy: "Leggi lo studio",
    problemStatement: "Problema",
    purpose: "Scopo",
    methods: "Materiali & Metodi",
    results: "Risultati",
    conclusions: "Conclusioni",
    evidenceSummary: "Sintesi delle evidenze: Le 8 categorie",
    peerReviewedStudies: "studi peer-reviewed dal",
    pubmedJournals: "PubMed & Riviste scientifiche",
    polymerToxicity: "Materiali resinosi & interazione cellulare",
    polymerDesc: "studi in vitro su TEGDMA, Bis-GMA, HEMA",
    highestToxicity: "risultati in vitro",
    cementProblem: "Cemento residuo & riscontri perimplantari",
    cementDesc: "studi sull'associazione tra cemento in eccesso e riscontri perimplantari",
    residue: "dei siti esaminati",
    moreLoss: "vs. 0,07 mm",
    cellAttachment: "Adesione cellulare",
    cellDesc: "studi sul titanio e i tessuti molli perimplantari",
    adhesion: "Adesione",
    hydrophilic: "Idrofilo",
    oneAbutment: "One-Abutment-One-Time",
    manipulationDesc: "meta-analisi su abutment definitivi e perdita ossea marginale",
    bone: "Osso",
    placement: "Posizionamento",
    surfaceModification: "Modifica della superficie",
    surfaceDesc: "Anodizzazione, nanotubi TiO₂, superfici idrofile",
    anodized: "Anodizzato",
    bioactive: "Bioattivo",
    emergenceProfile: "Profilo di emergenza",
    emergenceDesc: "Geometria specifica per il paziente, risultati estetici",
    individual: "Individuale",
    aesthetic: "Estetica",
    allergies: "Allergie & Biocompatibilità",
    allergiesDesc: "studi su tolleranza al titanio, allergie ai metalli",
    titanium: "Titanio",
    safe: "Tollerabilità",
    conversionDegree: "Grado di conversione",
    conversionDesc: "Polimerizzazione, monomeri residui, stampa 3D",
    incomplete: "Incompleto",
    release: "Rilascio",
    footnoteSubcategory: "Studi fanno parte delle categorie principali (C: Adesione cellulare, D: One-Abutment, A: Materiali resinosi)",
    scientificSolution: "Principi costruttivi del concetto EAP® (informazioni del produttore)",
    pureTitanium: "Titanio puro",
    pureTitaniumDesc: "In tutta l'area di contatto gengivale – nessun polimero nei tessuti molli",
    optimalCementation: "Posizione della giunzione adesiva",
    optimalCementationDesc: "Profilo di emergenza dritto, margini sopragengivali, massima distanza dall'osso consentita dal progetto",
    hydrophilicSurface: "Superficie idrofila",
    hydrophilicSurfaceDesc: "Rugosità e bagnabilità sono studiate in relazione all'adesione cellulare",
    oneTimeProtocol: "Protocollo One-Time",
    oneTimeProtocolDesc: "Abutment definitivo all'inserimento – nessuna manipolazione ripetuta",
    registerNow: "Registrati ora",
    requestConsultation: "Richiedi consulenza gratuita e campioni",
    scrollDown: "↓ Scorri verso il basso per analisi dettagliate di ogni categoria.",
    filterByCategory: "Filtra per categoria:",
    studiesInCategory: "studi nella categoria",
    keyFindingsTitle: "Risultati chiave della ricerca",
    keyFindingsSubtitle: "Risultati di singoli studi – nessuna garanzia per il caso individuale",
    finding1Title: "Residui di cemento nella perimplantite",
    finding1Text: "Nello studio è stato trovato cemento in eccesso nell'81 % dei siti esaminati con segni clinici e/o radiologici di malattia perimplantare. Lo studio descrive un'associazione, non una causalità universale.",
    finding1Source: "Wilson TG Jr. (2009)",
    finding2Title: "Perdita ossea da cementi metacrilato",
    finding2Text: "In questa analisi retrospettiva la perdita ossea perimplantare era maggiore con un cemento metacrilato con eccesso di cemento (1,37 mm) rispetto a un cemento ossido di zinco-eugenolo (0,07 mm).",
    finding2Source: "Korsch M, Walther W (2015)",
    finding3Title: "Citotossicità dei monomeri",
    finding3Text: "Indagini in vitro descrivono effetti citotossici di Bis-GMA, TEGDMA e HEMA in condizioni sperimentali definite. I risultati in vitro non sono automaticamente effetti clinici.",
    finding3Source: "Schweikl H et al. (2006)",
    finding4Title: "Vantaggi One-Abutment-One-Time",
    finding4Text: "La meta-analisi riporta in media 0,19 mm in meno di perdita ossea marginale con abutment definitivi – una media di studio, non un'affermazione sul caso individuale.",
    finding4Source: "Koutouzis T et al. (2017)",
    finding5Title: "Ottimizzazione della superficie in titanio",
    finding5Text: "Gli studi riportano valori di integrazione tissutale più favorevoli per le superfici in titanio idrofile rispetto a quelle convenzionali.",
    finding5Source: "Schwarz F et al. (2009)",
    advantagesTitle: "Caratteristiche costruttive degli abutment EAP® (informazioni del produttore)",
    advantage1: "Profilo di emergenza specifico – anatomicamente come un dente naturale",
    advantage2: "Giunzione adesiva il più lontano dall'osso possibile secondo il progetto – una caratteristica costruttiva, non una riduzione del rischio dimostrata",
    advantage3: "Aspetti estetici e relativi ai tessuti molli – nessun bordo grigio visibile previsto",
    advantage4: "Workflow One-Abutment-One-Time semplice – meno sedute",
    researchBegin2009: "2009 – Inizio della ricerca EAP®",
    researchBegin2009Desc: "La base scientifica per abutment specifici per il paziente",
    downloadSummary: "📄 Riepilogo studi (documento HTML di 3 pagine)",
    downloadHint: "Stato: Settembre 2026 – 155 studi verificati in banche dati scientifiche su principi di base; non studi specifici del prodotto EAP®",
  },
  es: {
    title: "Estudios Científicos | EAP® Abutments",
    description: "Estudios PubMed actuales sobre materiales de pilares, polímeros y adhesión celular.",
    heading: "Estudios Científicos",
    subtitle: "Investigación actual sobre materiales de pilares y adhesión celular",
    intro: "Los siguientes estudios examinan la comparación de diferentes materiales de pilares en cuanto a adhesión celular y biocompatibilidad.",
    readStudy: "Leer estudio",
    problemStatement: "Planteamiento del problema",
    purpose: "Propósito",
    methods: "Material y Métodos",
    results: "Resultados",
    conclusions: "Conclusiones",
    evidenceSummary: "Resumen de evidencia: Las 8 categorías",
    peerReviewedStudies: "estudios revisados por pares desde",
    pubmedJournals: "PubMed & Revistas científicas",
    polymerToxicity: "Materiales resinosos & interacción celular",
    polymerDesc: "estudios in vitro sobre TEGDMA, Bis-GMA, HEMA",
    highestToxicity: "hallazgos in vitro",
    cementProblem: "Cemento residual & hallazgos periimplantarios",
    cementDesc: "estudios sobre la asociación entre exceso de cemento y hallazgos periimplantarios",
    residue: "de los sitios examinados",
    moreLoss: "vs. 0,07 mm",
    cellAttachment: "Adhesión celular",
    cellDesc: "estudios sobre titanio y tejidos blandos periimplantarios",
    adhesion: "Adhesión",
    hydrophilic: "Hidrofílico",
    oneAbutment: "One-Abutment-One-Time",
    manipulationDesc: "metaanálisis sobre pilares definitivos y pérdida ósea marginal",
    bone: "Hueso",
    placement: "Colocación",
    surfaceModification: "Modificación de superficie",
    surfaceDesc: "Anodización, nanotubos TiO₂, superficies hidrofílicas",
    anodized: "Anodizado",
    bioactive: "Bioactivo",
    emergenceProfile: "Perfil de emergencia",
    emergenceDesc: "Geometría específica del paciente, resultados estéticos",
    individual: "Individual",
    aesthetic: "Estética",
    allergies: "Alergias & Biocompatibilidad",
    allergiesDesc: "estudios sobre tolerancia al titanio, alergias a metales",
    titanium: "Titanio",
    safe: "Tolerabilidad",
    conversionDegree: "Grado de conversión",
    conversionDesc: "Polimerización, monómeros residuales, impresión 3D",
    incomplete: "Incompleto",
    release: "Liberación",
    footnoteSubcategory: "Estudios son parte de categorías principales (C: Adhesión celular, D: One-Abutment, A: Materiales resinosos)",
    scientificSolution: "Principios de diseño del concepto EAP® (información del fabricante)",
    pureTitanium: "Titanio puro",
    pureTitaniumDesc: "En toda el área de contacto gingival – sin polímeros en tejidos blandos",
    optimalCementation: "Posición de la junta adhesiva",
    optimalCementationDesc: "Perfil de emergencia recto, márgenes supragingivales, la mayor distancia al hueso que permita el diseño",
    hydrophilicSurface: "Superficie hidrofílica",
    hydrophilicSurfaceDesc: "La rugosidad y la mojabilidad se estudian en relación con la adhesión celular",
    oneTimeProtocol: "Protocolo One-Time",
    oneTimeProtocolDesc: "Pilar definitivo en la inserción – sin manipulación repetida",
    registerNow: "Registrarse ahora",
    requestConsultation: "Solicitar consulta gratuita y muestras",
    scrollDown: "↓ Desplácese hacia abajo para análisis detallados de cada categoría.",
    filterByCategory: "Filtrar por categoría:",
    studiesInCategory: "estudios en la categoría",
    keyFindingsTitle: "Hallazgos clave de la investigación",
    keyFindingsSubtitle: "Resultados de estudios individuales – sin garantía para el caso individual",
    finding1Title: "Residuos de cemento en periimplantitis",
    finding1Text: "En el estudio se encontró exceso de cemento en el 81 % de los sitios examinados con signos clínicos y/o radiológicos de enfermedad periimplantaria. El estudio describe una asociación, no una causalidad universal.",
    finding1Source: "Wilson TG Jr. (2009)",
    finding2Title: "Pérdida ósea por cementos de metacrilato",
    finding2Text: "En este análisis retrospectivo, la pérdida ósea periimplantaria fue mayor con un cemento de metacrilato con exceso de cemento (1,37 mm) que con un cemento de óxido de zinc-eugenol (0,07 mm).",
    finding2Source: "Korsch M, Walther W (2015)",
    finding3Title: "Citotoxicidad de los monómeros",
    finding3Text: "Investigaciones in vitro describen efectos citotóxicos de Bis-GMA, TEGDMA y HEMA en condiciones experimentales definidas. Los hallazgos in vitro no son automáticamente efectos clínicos.",
    finding3Source: "Schweikl H et al. (2006)",
    finding4Title: "Beneficios One-Abutment-One-Time",
    finding4Text: "El metaanálisis informa en promedio 0,19 mm menos de pérdida ósea marginal con pilares definitivos – un promedio de estudio, no una afirmación sobre el caso individual.",
    finding4Source: "Koutouzis T et al. (2017)",
    finding5Title: "Optimización de la superficie de titanio",
    finding5Text: "Los estudios informan valores de integración tisular más favorables para superficies de titanio hidrofílicas que para superficies convencionales.",
    finding5Source: "Schwarz F et al. (2009)",
    advantagesTitle: "Características de diseño de los pilares EAP® (información del fabricante)",
    advantage1: "Perfil de emergencia específico – anatómicamente como un diente natural",
    advantage2: "Junta adhesiva tan alejada del hueso como el diseño permita – una característica de diseño, no una reducción de riesgo demostrada",
    advantage3: "Aspectos estéticos y relativos a tejidos blandos – sin bordes grises visibles previstos",
    advantage4: "Workflow One-Abutment-One-Time simple – menos sesiones",
    researchBegin2009: "2009 – Inicio de la investigación EAP®",
    researchBegin2009Desc: "La base científica para pilares específicos del paciente",
    downloadSummary: "📄 Resumen de estudios (documento HTML de 3 páginas)",
    downloadHint: "Estado: Septiembre 2026 – 155 estudios verificados en bases de datos científicas sobre principios subyacentes; no estudios específicos del producto EAP®",
  },
};

const Research = () => {
  const { language } = useLanguage();
  const t = translations[language] || translations.en;
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [decadeFilter, setDecadeFilter] = useState<string>("all");

  // Jahrzehnt-Optionen
  const decadeOptions = [
    { value: "all", label: { de: "Alle Jahre", en: "All Years", fr: "Toutes les années", nl: "Alle jaren", it: "Tutti gli anni", es: "Todos los años" } },
    { value: "1980-1990", label: { de: "1980–1990", en: "1980–1990", fr: "1980–1990", nl: "1980–1990", it: "1980–1990", es: "1980–1990" } },
    { value: "1991-2000", label: { de: "1991–2000", en: "1991–2000", fr: "1991–2000", nl: "1991–2000", it: "1991–2000", es: "1991–2000" } },
    { value: "2001-2010", label: { de: "2001–2010", en: "2001–2010", fr: "2001–2010", nl: "2001–2010", it: "2001–2010", es: "2001–2010" } },
    { value: "2011-2020", label: { de: "2011–2020", en: "2011–2020", fr: "2011–2020", nl: "2011–2020", it: "2011–2020", es: "2011–2020" } },
    { value: "2021-2030", label: { de: "2021–2030", en: "2021–2030", fr: "2021–2030", nl: "2021–2030", it: "2021–2030", es: "2021–2030" } },
  ];

  // Alle Studien aus der Datenbank abrufen
  const allStudies = getAllStudies();
  const studyCounts = getStudyCountByCategory();
  
  // Korrekte Studienanzahl pro Kategorie (filtert alle Studien nach category-Eigenschaft)
  const resinCount = studyCounts.resin;
  const cementCount = studyCounts.cement;
  const cellCount = studyCounts.cell;
  const oaotCount = studyCounts.oaot;
  const surfaceCount = studyCounts.surface;
  const emergenceCount = studyCounts.emergence;
  const allergyCount = studyCounts.allergy;
  const conversionCount = studyCounts.conversion;

  // Lokale Kategorie-Labels für die UI
  const categoryLabels: Record<string, { de: string; en: string; fr: string; nl: string; it: string; es: string }> = {
    all: { de: "Alle Studien", en: "All Studies", fr: "Toutes les études", nl: "Alle studies", it: "Tutti gli studi", es: "Todos los estudios" },
    resin: { de: "Resin-Materialien", en: "Resin Materials", fr: "Matériaux résineux", nl: "Resin-materialen", it: "Materiali resinosi", es: "Materiales resinosos" },
    cement: { de: "Residualzement", en: "Residual Cement", fr: "Ciment résiduel", nl: "Restcement", it: "Cemento residuo", es: "Cemento residual" },
    cell: { de: "Zellanhaftung", en: "Cell Attachment", fr: "Adhésion cellulaire", nl: "Celaanhechting", it: "Adesione cellulare", es: "Adhesión celular" },
    oaot: { de: "One-Abutment-One-Time", en: "One-Abutment-One-Time", fr: "One-Abutment-One-Time", nl: "One-Abutment-One-Time", it: "One-Abutment-One-Time", es: "One-Abutment-One-Time" },
    surface: { de: "Oberflächenmodifikation", en: "Surface Modification", fr: "Modification de surface", nl: "Oppervlaktemodificatie", it: "Modifica della superficie", es: "Modificación de superficie" },
    emergence: { de: "Emergenzprofil", en: "Emergence Profile", fr: "Profil d'émergence", nl: "Emergentieprofiel", it: "Profilo di emergenza", es: "Perfil de emergencia" },
    allergy: { de: "Allergien", en: "Allergies", fr: "Allergies", nl: "Allergieën", it: "Allergie", es: "Alergias" },
    conversion: { de: "Konversionsgrad", en: "Degree of Conversion", fr: "Degré de conversion", nl: "Conversiegraad", it: "Grado di conversione", es: "Grado de conversión" },
  };

  // Helper für Jahrzehnt-Filter
  const filterByDecade = (study: Study) => {
    if (decadeFilter === "all") return true;
    const [startYear, endYear] = decadeFilter.split("-").map(Number);
    return study.year >= startYear && study.year <= endYear;
  };

  const filteredStudies = allStudies
    .filter(study => activeFilter === "all" || study.topic === activeFilter)
    .filter(filterByDecade);

  // Helper um Text aus mehrsprachigem Objekt zu extrahieren
  const getText = (textObj: TranslatedText | string): string => {
    if (typeof textObj === 'string') return textObj;
    return textObj[language as Language] || textObj.en || textObj.de;
  };

  return (
    <>
      <Helmet>
        <title>{t.title}</title>
        <meta name="description" content={t.description} />
        <link rel="canonical" href="https://eap-abutments.com/research" />
        <meta property="og:title" content={t.title} />
        <meta property="og:description" content={t.description} />
        <meta property="og:url" content="https://eap-abutments.com/research" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": t.title,
          "description": t.description,
          "url": "https://eap-abutments.com/research",
          "about": "Peer-reviewed publications on dental implant abutment materials, cement residue, resin materials and monomers, in-vitro cytotoxicity, cell interaction and prosthetic protocols",
          "inLanguage": language,
          "isPartOf": { "@type": "WebSite", "name": "EAP® Abutments", "url": "https://eap-abutments.com" }
        })}</script>
      </Helmet>

      <Navigation />

      <main className="min-h-screen bg-background pt-24 pb-16">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Microscope className="w-4 h-4" />
              PubMed & Peer-Reviewed
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              {t.heading}
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              {t.subtitle}
            </p>
            
            {/* PDF Download Button */}
            <div className="mt-6">
              <a 
                href={trackedDownloadUrl("studien-zusammenfassung-2026", language)} 
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-3 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl"
              >
                <FileText className="w-5 h-5" />
                {t.downloadSummary}
              </a>
              <p className="text-sm text-muted-foreground mt-2">{t.downloadHint}</p>
            </div>
          </div>

          {/* ==================== HERO EVIDENCE SUMMARY ==================== */}
          <div className="mb-12 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-card via-card to-muted/30 border-2 border-primary/20 rounded-2xl overflow-hidden shadow-xl">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary/10 via-primary/5 to-transparent px-6 py-6 border-b border-primary/10">
                <div className="flex items-center gap-4">
                  <div className="bg-primary/20 p-3 rounded-xl">
                    <TrendingUp className="w-10 h-10 text-primary" />
                  </div>
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-foreground">
                      {t.evidenceSummary}
                    </h2>
                    <p className="text-muted-foreground text-sm md:text-base">
                      {allStudies.length} {t.peerReviewedStudies} {Math.min(...allStudies.map(s => s.year))}–{Math.max(...allStudies.map(s => s.year))} • {t.pubmedJournals}
                    </p>
                  </div>
                </div>
              </div>

              {/* Category Cards Grid - Responsive: 1 col mobile, 2 cols tablet, 3 cols desktop */}
              <div className="flex flex-wrap justify-center gap-3 sm:gap-4 p-4 sm:p-6">
                {/* Category A: Polymer Toxicity */}
                <div style={{ flex: '0 0 calc(33.333% - 1rem)' }}
                  className="group cursor-pointer bg-gradient-to-br from-destructive/5 to-destructive/10 rounded-xl p-3 sm:p-4 border border-destructive/20 hover:border-destructive/40 hover:shadow-lg transition-all duration-300 max-sm:!flex-[0_0_100%] max-lg:!flex-[0_0_calc(50%-0.5rem)]"
                  onClick={() => setActiveFilter("resin")}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="bg-destructive/15 p-2 sm:p-3 rounded-xl shrink-0 group-hover:bg-destructive/25 group-hover:scale-110 transition-all duration-300">
                      <FlaskConical className="w-6 h-6 sm:w-8 sm:h-8 text-destructive" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                        <span className="bg-destructive/20 text-destructive text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded">A</span>
                        <h3 className="font-bold text-sm sm:text-base text-foreground group-hover:text-destructive transition-colors truncate">
                          {t.polymerToxicity}
                        </h3>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3 line-clamp-2">
                        {resinCount} {t.polymerDesc}
                      </p>
                      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-destructive">Bis-GMA</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground truncate">{t.highestToxicity}</div>
                        </div>
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-destructive">1080 μM</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">TEGDMA</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Category B: Cement Problems */}
                <div style={{ flex: '0 0 calc(33.333% - 1rem)' }}
                  className="group cursor-pointer bg-gradient-to-br from-orange-500/5 to-orange-500/10 rounded-xl p-3 sm:p-4 border border-orange-500/20 hover:border-orange-500/40 hover:shadow-lg transition-all duration-300 max-sm:!flex-[0_0_100%] max-lg:!flex-[0_0_calc(50%-0.5rem)]"
                  onClick={() => setActiveFilter("cement")}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="bg-orange-500/15 p-2 sm:p-3 rounded-xl shrink-0 group-hover:bg-orange-500/25 group-hover:scale-110 transition-all duration-300">
                      <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                        <span className="bg-orange-500/20 text-orange-600 text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded">B</span>
                        <h3 className="font-bold text-sm sm:text-base text-foreground group-hover:text-orange-600 transition-colors truncate">
                          {t.cementProblem}
                        </h3>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3 line-clamp-2">
                        {cementCount} {t.cementDesc}
                      </p>
                      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-orange-600">81 %</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">{t.residue}</div>
                        </div>
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-orange-600">1,37 mm</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">{t.moreLoss}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Category C: Cell Attachment */}
                <div style={{ flex: '0 0 calc(33.333% - 1rem)' }}
                  className="group cursor-pointer bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-3 sm:p-4 border border-primary/20 hover:border-primary/40 hover:shadow-lg transition-all duration-300 max-sm:!flex-[0_0_100%] max-lg:!flex-[0_0_calc(50%-0.5rem)]"
                  onClick={() => setActiveFilter("cell")}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="bg-primary/15 p-2 sm:p-3 rounded-xl shrink-0 group-hover:bg-primary/25 group-hover:scale-110 transition-all duration-300">
                      <Bone className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                        <span className="bg-primary/20 text-primary text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded">C</span>
                        <h3 className="font-bold text-sm sm:text-base text-foreground group-hover:text-primary transition-colors truncate">
                          {t.cellAttachment}
                        </h3>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3 line-clamp-2">
                        {cellCount} {t.cellDesc}
                      </p>
                      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-primary">Ti / Zr</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">{t.adhesion}</div>
                        </div>
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-primary">↑</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">{t.hydrophilic}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Category D: One-Abutment-One-Time */}
                <div style={{ flex: '0 0 calc(33.333% - 1rem)' }}
                  className="group cursor-pointer bg-gradient-to-br from-destructive/5 to-destructive/10 rounded-xl p-3 sm:p-4 border border-destructive/20 hover:border-destructive/40 hover:shadow-lg transition-all duration-300 max-sm:!flex-[0_0_100%] max-lg:!flex-[0_0_calc(50%-0.5rem)]"
                  onClick={() => setActiveFilter("oaot")}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="bg-destructive/15 p-2 sm:p-3 rounded-xl shrink-0 group-hover:bg-destructive/25 group-hover:scale-110 transition-all duration-300">
                      <Hand className="w-6 h-6 sm:w-8 sm:h-8 text-destructive" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                        <span className="bg-destructive/20 text-destructive text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded">D</span>
                        <h3 className="font-bold text-sm sm:text-base text-foreground group-hover:text-destructive transition-colors truncate">
                          {t.oneAbutment}
                        </h3>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3 line-clamp-2">
                        {oaotCount} {t.manipulationDesc}
                      </p>
                      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-primary">−0,19 mm</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">{t.bone}</div>
                        </div>
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-primary">1×</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">{t.placement}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Category E: Surface Modification */}
                <div style={{ flex: '0 0 calc(33.333% - 1rem)' }}
                  className="group cursor-pointer bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-3 sm:p-4 border border-primary/20 hover:border-primary/40 hover:shadow-lg transition-all duration-300 max-sm:!flex-[0_0_100%] max-lg:!flex-[0_0_calc(50%-0.5rem)]"
                  onClick={() => setActiveFilter("surface")}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="bg-primary/15 p-2 sm:p-3 rounded-xl shrink-0 group-hover:bg-primary/25 group-hover:scale-110 transition-all duration-300">
                      <Sparkles className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                        <span className="bg-primary/20 text-primary text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded">E</span>
                        <h3 className="font-bold text-sm sm:text-base text-foreground group-hover:text-primary transition-colors truncate">
                          {t.surfaceModification}
                        </h3>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3 line-clamp-2">
                        {surfaceCount} {t.surfaceDesc}
                      </p>
                      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-primary">TiO₂</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">{t.anodized}</div>
                        </div>
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-primary">↑</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">{t.bioactive}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Category F: Emergence Profile */}
                <div style={{ flex: '0 0 calc(33.333% - 1rem)' }}
                  className="group cursor-pointer bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-3 sm:p-4 border border-primary/20 hover:border-primary/40 hover:shadow-lg transition-all duration-300 max-sm:!flex-[0_0_100%] max-lg:!flex-[0_0_calc(50%-0.5rem)]"
                  onClick={() => setActiveFilter("emergence")}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="bg-primary/15 p-2 sm:p-3 rounded-xl shrink-0 group-hover:bg-primary/25 group-hover:scale-110 transition-all duration-300">
                      <Award className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                        <span className="bg-primary/20 text-primary text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded">F</span>
                        <h3 className="font-bold text-sm sm:text-base text-foreground group-hover:text-primary transition-colors truncate">
                          {t.emergenceProfile}
                        </h3>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3 line-clamp-2">
                        {emergenceCount} {t.emergenceDesc}
                      </p>
                      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-primary">1:1</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">{t.individual}</div>
                        </div>
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-primary">★</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">{t.aesthetic}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Category G: Allergies */}
                <div style={{ flex: '0 0 calc(33.333% - 1rem)' }}
                  className="group cursor-pointer bg-gradient-to-br from-primary/5 to-primary/10 rounded-xl p-3 sm:p-4 border border-primary/20 hover:border-primary/40 hover:shadow-lg transition-all duration-300 max-sm:!flex-[0_0_100%] max-lg:!flex-[0_0_calc(50%-0.5rem)]"
                  onClick={() => setActiveFilter("allergy")}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="bg-primary/15 p-2 sm:p-3 rounded-xl shrink-0 group-hover:bg-primary/25 group-hover:scale-110 transition-all duration-300">
                      <Heart className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                        <span className="bg-primary/20 text-primary text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded">G</span>
                        <h3 className="font-bold text-sm sm:text-base text-foreground group-hover:text-primary transition-colors truncate">
                          {t.allergies}
                        </h3>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3 line-clamp-2">
                        {allergyCount} {t.allergiesDesc}
                      </p>
                      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-primary">Ti</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">{t.titanium}</div>
                        </div>
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-primary">✓</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">{t.safe}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Category H: Conversion Degree */}
                <div style={{ flex: '0 0 calc(33.333% - 1rem)' }}
                  className="group cursor-pointer bg-gradient-to-br from-orange-500/5 to-orange-500/10 rounded-xl p-3 sm:p-4 border border-orange-500/20 hover:border-orange-500/40 hover:shadow-lg transition-all duration-300 max-sm:!flex-[0_0_100%] max-lg:!flex-[0_0_calc(50%-0.5rem)]"
                  onClick={() => setActiveFilter("conversion")}
                >
                  <div className="flex items-start gap-2 sm:gap-3">
                    <div className="bg-orange-500/15 p-2 sm:p-3 rounded-xl shrink-0 group-hover:bg-orange-500/25 group-hover:scale-110 transition-all duration-300">
                      <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-orange-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-1.5 sm:mb-2">
                        <span className="bg-orange-500/20 text-orange-600 text-[10px] sm:text-xs font-bold px-1.5 sm:px-2 py-0.5 rounded">H</span>
                        <h3 className="font-bold text-sm sm:text-base text-foreground group-hover:text-orange-600 transition-colors truncate">
                          {t.conversionDegree}
                        </h3>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3 line-clamp-2">
                        {conversionCount} {t.conversionDesc}
                      </p>
                      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-orange-600">35-77%</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">{t.incomplete}</div>
                        </div>
                        <div className="bg-background/50 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 text-center">
                          <div className="text-xs sm:text-sm font-bold text-orange-600">↑</div>
                          <div className="text-[8px] sm:text-[10px] text-muted-foreground">{t.release}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
            </div>

              {/* ==================== EAP® SOLUTION HIGHLIGHT ==================== */}
              <div className="mx-6 mb-6">
                <div className="relative bg-gradient-to-r from-primary via-primary to-primary/90 rounded-2xl p-6 md:p-8 overflow-hidden">
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
                    <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
                  </div>
                  
                  <div className="relative z-10">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-white/20 p-2.5 rounded-xl">
                        <Shield className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-white/80" />
                          <span className="text-white/80 text-sm font-medium">
                            {t.scientificSolution}
                          </span>
                        </div>
                        <h3 className="text-2xl md:text-3xl font-bold text-white">
                          EAP® Abutments
                        </h3>
                      </div>
                    </div>

                    {/* Solution Grid */}
                    <div className="grid md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                        <div className="flex items-start gap-3">
                          <div className="bg-white/20 p-1.5 rounded-lg shrink-0 mt-0.5">
                            <CheckCircle className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-white mb-1">
                              {t.pureTitanium}
                            </h4>
                            <p className="text-white/80 text-sm">
                              {t.pureTitaniumDesc}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                        <div className="flex items-start gap-3">
                          <div className="bg-white/20 p-1.5 rounded-lg shrink-0 mt-0.5">
                            <CheckCircle className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-white mb-1">
                              {t.optimalCementation}
                            </h4>
                            <p className="text-white/80 text-sm">
                              {t.optimalCementationDesc}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                        <div className="flex items-start gap-3">
                          <div className="bg-white/20 p-1.5 rounded-lg shrink-0 mt-0.5">
                            <CheckCircle className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-white mb-1">
                              {t.hydrophilicSurface}
                            </h4>
                            <p className="text-white/80 text-sm">
                              {t.hydrophilicSurfaceDesc}
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                        <div className="flex items-start gap-3">
                          <div className="bg-white/20 p-1.5 rounded-lg shrink-0 mt-0.5">
                            <CheckCircle className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-white mb-1">
                              {t.oneTimeProtocol}
                            </h4>
                            <p className="text-white/80 text-sm">
                              {t.oneTimeProtocolDesc}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="flex flex-col sm:flex-row items-center gap-4">
                      <Link to="/#contact">
                        <Button 
                          size="lg" 
                          className="bg-white text-primary hover:bg-white/90 font-semibold px-8 group"
                        >
                          {t.registerNow}
                          <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                      <p className="text-white/70 text-sm">
                        {t.requestConsultation}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom Summary */}
              <div className="bg-gradient-to-r from-muted/50 via-muted/30 to-transparent px-6 py-4 border-t border-border">
                <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
                  <div className="bg-primary/10 p-2 rounded-lg shrink-0">
                    <Microscope className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t.scrollDown}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ==================== KEY FINDINGS SECTION ==================== */}
          <div className="max-w-6xl mx-auto mb-12">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                {t.keyFindingsTitle}
              </h2>
              <p className="text-muted-foreground">
                {t.keyFindingsSubtitle}
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
              {/* Finding 1 */}
              <Card className="border-destructive/20 bg-gradient-to-br from-destructive/5 to-transparent">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-5 h-5 text-destructive" />
                    <span className="text-destructive text-xs font-bold">81 %</span>
                  </div>
                  <CardTitle className="text-base">{t.finding1Title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-2">{t.finding1Text}</p>
                  <p className="text-xs text-muted-foreground/70">{t.finding1Source}</p>
                </CardContent>
              </Card>

              {/* Finding 2 */}
              <Card className="border-orange-500/20 bg-gradient-to-br from-orange-500/5 to-transparent">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-5 h-5 text-orange-500" />
                    <span className="text-orange-600 text-xs font-bold">1,37 mm</span>
                  </div>
                  <CardTitle className="text-base">{t.finding2Title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-2">{t.finding2Text}</p>
                  <p className="text-xs text-muted-foreground/70">{t.finding2Source}</p>
                </CardContent>
              </Card>

              {/* Finding 3 */}
              <Card className="border-destructive/20 bg-gradient-to-br from-destructive/5 to-transparent">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2 mb-1">
                    <FlaskConical className="w-5 h-5 text-destructive" />
                    <span className="text-destructive text-xs font-bold">DNA</span>
                  </div>
                  <CardTitle className="text-base">{t.finding3Title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-2">{t.finding3Text}</p>
                  <p className="text-xs text-muted-foreground/70">{t.finding3Source}</p>
                </CardContent>
              </Card>

              {/* Finding 4 */}
              <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-5 h-5 text-primary" />
                    <span className="text-primary text-xs font-bold">-0,19 mm</span>
                  </div>
                  <CardTitle className="text-base">{t.finding4Title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-2">{t.finding4Text}</p>
                  <p className="text-xs text-muted-foreground/70">{t.finding4Source}</p>
                </CardContent>
              </Card>

              {/* Finding 5 */}
              <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2 mb-1">
                    <Bone className="w-5 h-5 text-primary" />
                    <span className="text-primary text-xs font-bold">Ti</span>
                  </div>
                  <CardTitle className="text-base">{t.finding5Title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-2">{t.finding5Text}</p>
                  <p className="text-xs text-muted-foreground/70">{t.finding5Source}</p>
                </CardContent>
              </Card>

              {/* Advantages Summary */}
              <Card className="border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5">
                <CardHeader className="pb-2">
                  <div className="flex items-center gap-2 mb-1">
                    <Award className="w-5 h-5 text-primary" />
                    <span className="text-primary text-xs font-bold">EAP®</span>
                  </div>
                  <CardTitle className="text-base">{t.advantagesTitle}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1">
                  <p className="text-xs text-muted-foreground flex items-start gap-1">
                    <CheckCircle className="w-3 h-3 text-primary shrink-0 mt-0.5" />
                    {t.advantage1}
                  </p>
                  <p className="text-xs text-muted-foreground flex items-start gap-1">
                    <CheckCircle className="w-3 h-3 text-primary shrink-0 mt-0.5" />
                    {t.advantage2}
                  </p>
                  <p className="text-xs text-muted-foreground flex items-start gap-1">
                    <CheckCircle className="w-3 h-3 text-primary shrink-0 mt-0.5" />
                    {t.advantage3}
                  </p>
                  <p className="text-xs text-muted-foreground flex items-start gap-1">
                    <CheckCircle className="w-3 h-3 text-primary shrink-0 mt-0.5" />
                    {t.advantage4}
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Intro */}
          <div className="bg-secondary/30 border border-border rounded-xl p-6 mb-8 max-w-4xl mx-auto">
            <p className="text-muted-foreground text-center">
              {t.intro}
            </p>
          </div>

          {/* Category Filter */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4">
              <div className="flex items-center gap-2">
                <Filter className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">
                  {t.filterByCategory}
                </span>
              </div>
              
              {/* Jahrzehnt Dropdown */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <Select value={decadeFilter} onValueChange={setDecadeFilter}>
                  <SelectTrigger className="w-[160px] h-9 bg-background border-input">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border shadow-md">
                    {decadeOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label[language as keyof typeof option.label] || option.label.en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {Object.entries(categoryLabels).map(([key, label]) => {
                const count = studyCounts[key] || 0;
                return (
                  <Button
                    key={key}
                    variant={activeFilter === key ? "default" : "outline"}
                    size="sm"
                    onClick={() => setActiveFilter(key)}
                    className={`transition-all ${
                      activeFilter === key 
                        ? "bg-primary text-primary-foreground shadow-md" 
                        : "hover:bg-primary/10"
                    }`}
                  >
                    {label[language as keyof typeof label] || label.en}
                    <span className={`ml-2 px-1.5 py-0.5 rounded text-xs ${
                      activeFilter === key 
                        ? "bg-primary-foreground/20 text-primary-foreground" 
                        : "bg-muted text-muted-foreground"
                    }`}>
                      {count}
                    </span>
                  </Button>
                );
              })}
            </div>
            
            {(activeFilter !== "all" || decadeFilter !== "all") && (
              <p className="text-sm text-muted-foreground mt-3">
                {filteredStudies.length} {t.studiesInCategory}
                {activeFilter !== "all" && ` "${categoryLabels[activeFilter]?.[language as keyof typeof categoryLabels[typeof activeFilter]] || categoryLabels[activeFilter]?.en}"`}
                {decadeFilter !== "all" && ` (${decadeFilter})`}
              </p>
            )}
          </div>

          {/* Studies Grid */}
          <div className="grid gap-6 max-w-4xl mx-auto">
            {filteredStudies.map((study, index) => {
              // Check if this is the first 2009 study (beginning of EAP research)
              const isFirst2009Study = study.year === 2009 && 
                (index === 0 || filteredStudies[index - 1].year !== 2009);
              
              return (
                <div key={`${study.category}-${study.id}-${index}`}>
                  {/* 2009 Milestone Marker */}
                  {isFirst2009Study && (
                    <div className="mb-6 relative">
                      <div className="absolute left-0 right-0 top-1/2 h-px bg-gradient-to-r from-transparent via-primary/50 to-transparent" />
                      <div className="relative flex justify-center">
                        <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground px-6 py-3 rounded-xl shadow-lg flex items-center gap-3">
                          <Sparkles className="w-5 h-5" />
                          <div className="text-center">
                            <div className="font-bold text-lg">{t.researchBegin2009}</div>
                            <div className="text-sm text-primary-foreground/80">{t.researchBegin2009Desc}</div>
                          </div>
                          <Sparkles className="w-5 h-5" />
                        </div>
                      </div>
                    </div>
                  )}
              
              <Card className={`overflow-hidden hover:shadow-lg transition-shadow ${study.year === 2009 ? 'ring-2 ring-primary/30 bg-primary/5' : ''}`}>
                <CardHeader className="pb-3">
                  <div className="flex flex-wrap gap-2 mb-2">
                    <Badge className="bg-primary text-primary-foreground font-bold">
                      #{index + 1}
                    </Badge>
                    {/* EAP-Relevanz Badge */}
                    {(() => {
                      const relevance = getStudyRelevance(study);
                      return (
                        <Badge className={`${getRelevanceColor(relevance)} font-semibold`}>
                          {relevance} - {getRelevanceLabel(relevance, language as Language)}
                        </Badge>
                      );
                    })()}
                    <Badge variant="secondary" className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {study.year}
                    </Badge>
                    <Badge variant="outline">
                      {getTypeLabelFromData(study.type, language as Language)}
                    </Badge>
                    <Badge variant="outline" className="flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      {study.journal}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg leading-tight">
                    {getText(study.title)}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {study.authors}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Structured Summary */}
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="font-semibold text-primary">{t.problemStatement}: </span>
                      <span className="text-muted-foreground">{getText(study.structured.problemStatement)}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-primary">{t.purpose}: </span>
                      <span className="text-muted-foreground">{getText(study.structured.purpose)}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-primary">{t.methods}: </span>
                      <span className="text-muted-foreground">{getText(study.structured.methods)}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-primary">{t.results}: </span>
                      <span className="text-muted-foreground">{getText(study.structured.results)}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-primary">{t.conclusions}: </span>
                      <span className="text-muted-foreground">{getText(study.structured.conclusions)}</span>
                    </div>
                  </div>

                  {study.url && study.url.startsWith('http') && (
                    <a
                      href={study.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      referrerPolicy="no-referrer"
                      className="inline-flex items-center gap-2 text-primary hover:text-primary/80 text-sm font-medium transition-colors cursor-pointer"
                      onClick={(e) => {
                        // Don't let the card/container swallow the click.
                        // The plain href opens the study (PubMed/publisher) directly in a new tab.
                        e.stopPropagation();
                      }}
                    >
                      {t.readStudy}
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </CardContent>
              </Card>
              </div>
              );
            })}
          </div>

          {/* ==================== INTERNE WEITERFÜHRENDE SEITEN ==================== */}
          <nav aria-label={language === "de" ? "Weiterführende Seiten" : "Related pages"} className="max-w-4xl mx-auto mt-16">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              {language === "de" ? "Weiterführende Seiten" : "Related pages"}
            </h2>
            <ul className="grid gap-3 sm:grid-cols-3">
              <li>
                <Link to="/fuer-patienten" className="block h-full rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors">
                  <span className="font-medium text-foreground">
                    {language === "de" ? "Informationen für Patienten" : "Information for patients"}
                  </span>
                </Link>
              </li>
              <li>
                <Link to="/ti-base-oder-individuelles-abutment" className="block h-full rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors">
                  <span className="font-medium text-foreground">
                    {language === "de" ? "Ti-Base oder individuelles Abutment" : "Ti-base or custom abutment"}
                  </span>
                </Link>
              </li>
              <li>
                <Link to="/faq" className="block h-full rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors">
                  <span className="font-medium text-foreground">
                    {language === "de" ? "Häufige Fragen" : "Frequently asked questions"}
                  </span>
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </main>


      <Footer />
    </>
  );
};

export default Research;
